// "Klaviyo-jelek" under the webshop events (7.2 M2): every stream the flows in
// shadow read from Klaviyo — a country's metric, list or segment — and how far
// it is stored. The watermark is the promise: every event up to it is here,
// and the engine decides a step only once every stream it reads is past it.
// A lag of more than a few ticks is a stream that is stuck; the error says why.

import type { ColumnDef } from "@tanstack/react-table";
import { fetchKlaviyoSignalSources } from "@/lib/api";
import type { KlaviyoSignalSource } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/emc/DataTable";
import { EnumText } from "@/components/emc/EnumText";
import { Country } from "@/components/emc/Flag";
import { StatusPill } from "@/components/emc/StatusPill";
import { count, minute } from "@/components/emc/format";
import { EmptyState, ErrorState } from "@/components/emc/states";

/** "12 perc", "3 óra 5 perc", "2 nap" — how far behind the watermark is. */
function lag(seconds: number | null, walking: boolean): string {
  if (seconds === null) return walking ? "első visszatöltés…" : "—";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} perc`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} óra ${minutes % 60} perc`;
  return `${Math.floor(hours / 24)} nap`;
}

const COLUMNS: ColumnDef<KlaviyoSignalSource>[] = [
  { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
  {
    id: "kind",
    header: "Fajta",
    cell: ({ row }) => <EnumText domain="signalKind" value={row.original.kind} />,
  },
  {
    id: "name",
    header: "Név",
    cell: ({ row }) => (
      <span>
        {row.original.name} <span className="data-mono text-muted-foreground">{row.original.source_id}</span>
      </span>
    ),
  },
  {
    id: "state",
    header: "Állapot",
    cell: ({ row }) => <StatusPill domain="signalSource" state={row.original.state} />,
  },
  {
    id: "watermark",
    header: "Vízjel",
    cell: ({ row }) => minute(row.original.complete_until),
    meta: { mono: true, muted: true },
  },
  {
    id: "lag",
    header: "Késés",
    cell: ({ row }) => lag(row.original.lag_seconds, row.original.walking),
    meta: { mono: true },
  },
  {
    id: "stored",
    header: "Tárolt sor",
    cell: ({ row }) => count(row.original.stored),
    meta: { mono: true },
  },
  {
    id: "stragglers",
    header: "Késve jött",
    cell: ({ row }) => count(row.original.stragglers),
    meta: { mono: true },
  },
  {
    id: "error",
    header: "Utolsó hiba",
    cell: ({ row }) =>
      row.original.last_error === null ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span title={row.original.last_error}>
          {minute(row.original.last_error_at)} · {row.original.last_error.slice(0, 80)}
        </span>
      ),
    meta: { wrap: true },
  },
];

export function KlaviyoSignalSources() {
  const [sources] = useLoad(fetchKlaviyoSignalSources, []);
  const rows = sources.data ?? [];
  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Klaviyo-jelek</CardTitle>
        <CardDescription>
          Amit az árnyék-módú flow-k a Klaviyóból olvasnak: országonként a metrikák eseményei és a listák,
          szegmensek csatlakozásai, ötpercenként, csak olvasva. A vízjelig minden esemény bent van; a motor csak
          addig dönt. A „késve jött” azokat számolja, amelyeket a napi újraolvasás a vízjel alatt talált.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {sources.error !== null && <ErrorState message={sources.error} className="mb-4" />}
        {sources.data !== null && rows.length === 0 ? (
          <EmptyState title="Még egy Klaviyo-adatfolyam sincs beállítva." />
        ) : (
          <DataTable
            label="Klaviyo-jelek"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            columns={COLUMNS}
            data={rows}
            getRowId={(row) => String(row.id)}
            loading={sources.data === null && sources.error === null}
          />
        )}
      </CardContent>
    </Card>
  );
}
